import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../../whatsapp/function/token';
import domain from '../../reusecomponent/domain';
import { FaPlus, FaEye, FaSpinner, FaInbox, FaSearch, FaFilter, FaTicketAlt, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

export default function TicketList() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const userToken = getToken();
      const response = await axios.get(`${domain()}/tickets`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Accept': 'application/json'
        }
      });
      if (response.data && response.data.status) {
        setTickets(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch tickets.');
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err.response?.data?.message || 'Something went wrong while fetching tickets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#e0f7fa', color: '#00838f', fontSize: '12px' }}>
            Open
          </span>
        );
      case 'closed':
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#f1f3f5', color: '#495057', fontSize: '12px' }}>
            Closed
          </span>
        );
      case 'closed_by_admin':
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#fde8e8', color: '#c81e1e', fontSize: '12px' }}>
            Closed
          </span>
        );
      case 'closed_by_user':
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#f1f3f5', color: '#495057', fontSize: '12px' }}>
            Closed
          </span>
        );
      default:
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#f1f3f5', color: '#495057', fontSize: '12px' }}>
            {status}
          </span>
        );
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch =
      ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.ticket_no?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'open' && ticket.status === 'open') ||
      (statusFilter === 'closed' && (ticket.status === 'closed' || ticket.status === 'closed_by_admin' || ticket.status === 'closed_by_user'));

    return matchesSearch && matchesStatus;
  });

  const openTicketsCount = tickets.filter(t => t.status === 'open').length;
  const closedTicketsCount = tickets.filter(t => t.status === 'closed' || t.status === 'closed_by_admin' || t.status === 'closed_by_user').length;

  return (
    <div className="col-lg-12 grid-margin stretch-card p-0 px-sm-3">
      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', backgroundColor: '#ffffff' }}>
        <div className="card-body">

          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
            <div>
              <h5 className="mb-0 fw-bold" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.25rem' }}>Support Tickets</h5>
              <p className="text-muted mb-0 small">Raise and manage your support queries and requests in real-time.</p>
            </div>
            <button
              className="btn text-white px-4 py-2 fw-semibold d-flex align-items-center gap-2 shadow-sm"
              style={{
                backgroundColor: '#075E54',
                borderColor: '#075E54',
                borderRadius: '10px',
                fontSize: '14px'
              }}
              onClick={() => navigate('/client-area/whatsapp/tickets/create')}
            >
              <FaPlus size={10} />
              Raise New Ticket
            </button>
          </div>

          {/* Stats Cards Section */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-4">
              <div className="p-3 shadow-none" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <span className="text-uppercase tracking-wider small fw-bold text-secondary d-block mb-1" style={{ fontSize: '10px' }}>Total Tickets</span>
                    <h4 className="fw-bold mb-0 text-dark" style={{ fontWeight: 800 }}>{tickets.length}</h4>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(7, 94, 84, 0.08)', color: '#075E54', width: '45px', height: '45px' }}>
                    <FaTicketAlt size={16} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="p-3 shadow-none" style={{ borderRadius: '12px', border: '1px solid #bae6fd', backgroundColor: '#e0f2fe' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <span className="text-uppercase tracking-wider small fw-bold d-block mb-1" style={{ fontSize: '10px', color: '#0369a1' }}>Open Tickets</span>
                    <h4 className="fw-bold mb-0" style={{ color: '#0284c7', fontWeight: 800 }}>{openTicketsCount}</h4>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(2, 132, 199, 0.08)', color: '#0284c7', width: '45px', height: '45px' }}>
                    <FaExclamationCircle size={16} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="p-3 shadow-none" style={{ borderRadius: '12px', border: '1px solid #bbf7d0', backgroundColor: '#f0fdf4' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <span className="text-uppercase tracking-wider small fw-bold d-block mb-1" style={{ fontSize: '10px', color: '#15803d' }}>Resolved Tickets</span>
                    <h4 className="fw-bold mb-0" style={{ color: '#16a34a', fontWeight: 800 }}>{closedTicketsCount}</h4>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(22, 163, 74, 0.08)', color: '#16a34a', width: '45px', height: '45px' }}>
                    <FaCheckCircle size={16} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="row g-3 align-items-center mb-4">
            <div className="col-12 col-md-8 col-lg-9">
              <div className="position-relative">
                <span className="position-absolute top-50 translate-middle-y start-0 ms-3 text-muted">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control text-dark ps-5"
                  placeholder="Search by ticket no or subject..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    padding: '10px 10px 10px 45px',
                    boxShadow: 'none',
                    fontSize: '14px',
                    backgroundColor: '#ffffff'
                  }}
                />
              </div>
            </div>
            <div className="col-12 col-md-4 col-lg-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small fw-bold text-nowrap">Filter Status:</span>
                <select
                  className="form-select text-dark"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    padding: '10px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    backgroundColor: '#ffffff'
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="open">Active / Open</option>
                  <option value="closed">Resolved / Closed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Content Area */}
          {loading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <FaSpinner className="spinner-border border-0 text-success p-0 mb-3" style={{ width: '3rem', height: '3rem', color: '#075E54' }} />
              <span className="text-secondary fw-semibold">Fetching tickets, please wait...</span>
            </div>
          ) : error ? (
            <div className="alert alert-danger border-0 rounded-3 p-4 text-center" role="alert" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <h4 className="alert-heading fw-bold mb-2">Error Occurred</h4>
              <p className="mb-0 text-muted">{error}</p>
              <button className="btn btn-outline-danger mt-3 px-4 rounded-pill" onClick={fetchTickets}>
                Try Again
              </button>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-4 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: '80px', height: '80px', backgroundColor: '#f8fafc' }}>
                <FaInbox size={32} className="text-secondary opacity-60" />
              </div>
              <h4 className="fw-bold mb-2 text-dark">No Tickets Found</h4>
              <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '450px', fontSize: '14px' }}>
                {searchTerm || statusFilter !== 'all'
                  ? "No tickets match your search filters. Try adjusting your query."
                  : "You haven't raised any support tickets yet. If you have an issue, raise a ticket now."}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <button
                  className="btn text-white px-4 py-2 fw-semibold shadow-sm"
                  style={{
                    backgroundColor: '#075E54',
                    borderColor: '#075E54',
                    borderRadius: '10px'
                  }}
                  onClick={() => navigate('/client-area/whatsapp/tickets/create')}
                >
                  Raise Your First Ticket
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive" style={{ overflow: 'auto !important', height: 'calc(100% - 50px)', borderRadius: '12px' }}>
              <table className="table payment-history-table align-middle mb-0">
                <thead>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <th className="py-3 px-4 text-secondary fw-bold" style={{ width: '220px', fontSize: '11px', letterSpacing: '0.8px' }}>Ticket Info</th>
                    <th className="py-3 text-secondary fw-bold" style={{ fontSize: '11px', letterSpacing: '0.8px' }}>Subject</th>
                    <th className="py-3 text-secondary fw-bold" style={{ width: '160px', fontSize: '11px', letterSpacing: '0.8px' }}>Status</th>
                    <th className="py-3 text-secondary fw-bold" style={{ width: '200px', fontSize: '11px', letterSpacing: '0.8px' }}>Created Date</th>
                    <th className="py-3 text-center text-secondary fw-bold" style={{ width: '150px', fontSize: '11px', letterSpacing: '0.8px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map((ticket) => (
                    <tr key={ticket.id} className="table-row">
                      <td className="py-3 px-4">
                        <span className="fw-bold text-dark d-block" style={{ fontSize: '14px' }}>#{ticket.ticket_no}</span>
                        <span className="text-muted small">ID: {ticket.id}</span>
                      </td>
                      <td className="py-3">
                        <span className="fw-medium text-dark text-truncate d-inline-block" style={{ maxWidth: '350px', fontSize: '14px' }}>
                          {ticket.subject}
                        </span>
                      </td>
                      <td className="py-3">{getStatusBadge(ticket.status)}</td>
                      <td className="py-3">
                        <span className="text-dark d-block fw-medium" style={{ fontSize: '13.5px' }}>
                          {new Date(ticket.created_at).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                        <span className="text-muted small">
                          {new Date(ticket.created_at).toLocaleTimeString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </td>
                      <td className="py-3 text-center">
                        <button
                          className="btn btn-outline-success btn-sm px-3 py-1-5 d-inline-flex align-items-center gap-2 fw-semibold"
                          style={{ color: '#075E54', borderColor: '#cbd5e1', borderRadius: '8px', fontSize: '12.5px' }}
                          onClick={() => navigate(`/client-area/whatsapp/tickets/detail/${btoa(ticket.id)}`)}
                        >
                          <FaEye size={12} />
                          View Chat
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
